import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect } from 'react';
import { contractManagement } from '../../../../services/utils/axios';
import { errorCodes } from '../../../../services/utils/errorCodes';
import { format } from 'date-fns';
import { useAuth } from '../../../../services/contexts/AuthContext';
import {
  getBillingCycleData,
  getUserTypeData,
} from '../../../../services/utils/translations-constants.js';
import {
  base64ToArrayBuffer,
  getBase64,
} from '../../../../services/utils/pdf-byte';
import { useSWRConfig } from 'swr';
import { formatDecimal } from '../../../../services/utils/format-numbers';
import { useTranslation } from 'react-i18next';
import OverflowText from '../../../../components/OverflowText';

/**
 * @param {PropTypes.InferProps<NutzerAnlegen.propTypes>} props
 */

function ConfirmUser({
  payload,
  openFeedback,
  submit,
  setError,
  setrfidError,
  setrfidSError,
  setemailError,
  setEndCustomerId,
  setDublicatedEndCustomerIdError,
  setSubmit,
  editFlag,
  contract,
}) {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { mutate } = useSWRConfig();
  const { userTypeMap } = getUserTypeData(t);
  const { billingCycleMap } = getBillingCycleData(t);

  useEffect(() => {
    if (submit > 0) {
      if (editFlag) {
        updateUser();
      } else {
        createUser();
      }
    }
    setSubmit(0);
  }, [submit]);
  if (!payload) return null;

  const dbKommunikationsadresse = payload
    ? [
        payload?.contractAddress?.careOf
          ? `c/o ${payload?.contractAddress?.careOf} `
          : '',
        payload?.contractAddress?.street + ' ',
        payload?.contractAddress?.zipCode +
          ' ' +
          payload?.contractAddress?.city,
      ]
    : null;

  const dbRechnungsnschrift = payload
    ? payload?.useCustomBillingDetails
      ? [
          payload?.contractBillingAddress?.name + ', ',
          payload?.contractBillingAddress?.careOf
            ? `c/o ${payload?.contractBillingAddress?.careOf} `
            : '',
          payload?.contractBillingAddress?.street + ' ',
          payload?.contractBillingAddress?.zipCode +
            ' ' +
            payload?.contractBillingAddress?.city,
        ]
      : [
          payload?.firstName + ' ' + payload?.lastName + ', ',
          dbKommunikationsadresse,
        ]
    : null;

  const renderData = {
    EndCustomerId: payload?.endCustomerId,
    ContractNumber: payload?.contractNumber,
    UserType: userTypeMap[payload?.userType] || payload?.userType,
    Name: payload?.firstName + ' ' + payload?.lastName,
    Kommunikationsadresse: dbKommunikationsadresse,
    Rechnungsnschrift: dbRechnungsnschrift,
    'E-Mail': payload?.email,
    billingEmail: payload?.billingEmail,
    Standort: payload?.locationId
      ? payload?.locationId + ':' + payload?.locationName
      : null,
    Parkplaznummer: payload?.parkingLotNumbers,
    'Authentifizierung über':
      payload?.authType === 'APP'
        ? 'Mobile App'
        : payload?.authType === 'APP_AND_RFID'
          ? 'APP, RFID'
          : 'RFID-Karte',
    'RFID-Tags': payload?.rfidTags,
    'RFID-serial-numbers': payload?.rfidSTags,
    Abrechnungsart:
      payload?.billingType === 'WITH_BILLING'
        ? 'Kostenpflichtig'
        : 'Kostenfrei',
    Abrechnungszyklus:
      payload?.billingType === 'WITH_BILLING' && payload?.billingCycle
        ? billingCycleMap[payload?.billingCycle] || payload?.billingCycle
        : null,
    'Nutzungs- und Ladestromvertrag': payload?.contractInfo?.contractPDF?.name,
    Startdatum:
      payload?.contractInfo?.startDate != undefined
        ? format(new Date(payload?.contractInfo?.startDate), 'dd.MM.yyyy')
        : null,
    Enddatum:
      payload?.contractInfo?.endDate != undefined &&
      payload?.contractInfo?.endDate != ''
        ? format(new Date(payload?.contractInfo?.endDate), 'dd.MM.yyyy')
        : t('unlimited'),
    Nutzungstarif:
      payload?.billingType === 'WITH_BILLING'
        ? payload?.contractInfo?.fixTariff != undefined
          ? `${formatDecimal(payload?.contractInfo?.fixTariff)} €/Monat`
          : null
        : null,
    Ladestromtarif:
      payload?.billingType === 'WITH_BILLING'
        ? payload?.contractInfo?.varTariff != undefined
          ? `${formatDecimal(payload?.contractInfo?.varTariff)} €/kWh`
          : null
        : null,
    Bereitstellungsgebühr:
      payload?.billingType === 'WITH_BILLING'
        ? payload?.contractInfo?.onboardingFee != undefined
          ? `${formatDecimal(payload?.contractInfo?.onboardingFee)} €`
          : null
        : null,
    Ladevorgangsgebühr:
      payload?.billingType === 'WITH_BILLING'
        ? payload?.contractInfo?.fixTariffPerChargingRequest != undefined
          ? `${formatDecimal(payload?.contractInfo?.fixTariffPerChargingRequest)} €/Ladevorgang`
          : null
        : null,
    ContractTaxRate:
      payload?.billingType === 'WITH_BILLING' &&
      payload?.contractTaxRates?.contractTaxRateFix != null &&
      payload?.contractTaxRates?.contractTaxRateFix !== undefined
        ? formatDecimal(payload?.contractTaxRates?.contractTaxRateFix * 100) +
          ' %'
        : null,

    Zahlungsmethode:
      payload?.billingType === 'NO_BILLING'
        ? null
        : payload?.paymentMethod === 'BANK_TRANSFER'
          ? t('rechnung')
          : payload?.paymentMethod === 'STRIPE_SEPA'
            ? t('sepaStripemode')
            : t('sepa'),
    virtualIbanOfUser:
      payload?.paymentMethod === 'BANK_TRANSFER' &&
      payload?.contractPaymentDetails?.contractIban
        ? payload?.contractPaymentDetails?.contractIban
        : null,
    invoiceRemark:
      payload?.billingType === 'WITH_BILLING'
        ? payload?.contractPaymentDetails?.invoiceRemark
        : null,
    Mandatsreferenz:
      (payload?.paymentMethod === 'SEPA' ||
        payload?.paymentMethod === 'STRIPE_SEPA') &&
      payload?.contractPaymentDetails?.contractMandateReference
        ? payload?.contractPaymentDetails?.contractMandateReference
        : null,
    'Debitorkonto-Nr.':
      (payload?.paymentMethod === 'SEPA' ||
        payload?.paymentMethod === 'BANK_TRANSFER') &&
      payload?.erpSystemParameters?.debitAccountNr
        ? payload?.erpSystemParameters?.debitAccountNr
        : null,
    'Kostenstellen-Nr':
      (payload?.paymentMethod === 'SEPA' ||
        payload?.paymentMethod === 'BANK_TRANSFER') &&
      payload?.erpSystemParameters?.costCenterNumber
        ? payload?.erpSystemParameters?.costCenterNumber
        : null,
    Umsatzsteuerkennung:
      (payload?.paymentMethod === 'SEPA' ||
        payload?.paymentMethod === 'BANK_TRANSFER') &&
      payload?.erpSystemParameters?.vatId
        ? `${payload?.erpSystemParameters?.vatId} %`
        : null,
  };

  // console.log('renderData :', renderData);
  const createUser = async () => {
    try {
      if (payload?.rfidTags) {
        let trimmedRFId = payload?.rfidTags.map((element) => {
          return element.trim();
        });
        payload.rfidTags = trimmedRFId;
      }
      if (payload?.rfidSTags) {
        let trimmedSRFId = payload?.rfidSTags.map((element) => {
          return element.trim();
        });
        payload.rfidSerialId = trimmedSRFId;
        delete payload?.rfidSTags;
      }
      const file = payload?.contractInfo.contractPDF;
      let contractPdfInByteArrayForm;
      if (file) {
        const _base64 = await getBase64(file);
        const [, base64] = _base64.split(',');

        contractPdfInByteArrayForm = Array.from(
          new Uint8Array(base64ToArrayBuffer(base64))
        );
      }
      const newPayload = {
        ...payload,
        cpoId: payload?.cpoId,
        contractInfo: {
          ...payload?.contractInfo,
          contractPdfInByteArrayForm,
          startDate: format(payload?.contractInfo.startDate, 'yyyy-MM-dd'),
          endDate: payload?.contractInfo?.endDate
            ? format(new Date(payload?.contractInfo?.endDate), 'yyyy-MM-dd')
            : null,
        },
        locationId: payload?.locationId
          ? payload?.locationId.split(':')[0]
          : null,
      };
      delete newPayload.contractInfo.contractPDF;
      const newPayloadEntries = Object.entries(newPayload)
        .map(([key, value]) => {
          if (value === undefined || value === null || value === '')
            return null;
          if (typeof value === 'object' && !Array.isArray(value)) {
            for (const field in value) {
              if (
                value[field] === undefined ||
                value[field] === null ||
                value[field] === ''
              ) {
                delete value[field];
              }
            }
          }
          return [key, value];
        })
        .filter(Boolean);
      const filteredPayload = Object.fromEntries(newPayloadEntries);
      await contractManagement.post('/create-contract', filteredPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      openFeedback();
      mutate(['/contracts/operator', token, 'contractManagement']);
    } catch (error) {
      if (error?.error?.errorCodeResponse === errorCodes.rfIdExists) {
        setrfidError(error?.error?.errorDetail);
      } else if (
        error?.error?.errorCodeResponse === errorCodes.rfIdSerialNotExists
      ) {
        setrfidSError(error?.error?.errorDetail);
      } else if (
        error?.error?.errorCodeResponse === errorCodes.duplicateEmail
      ) {
        setemailError(true);
        setEndCustomerId(error?.error?.errorMessage);
      } else if (
        error?.error?.errorCodeResponse === errorCodes.dublicatedEndCustomerId
      ) {
        setDublicatedEndCustomerIdError(true);
      } else {
        setError(true);
      }
    }
  };
  const updateUser = async () => {
    try {
      if (payload?.rfidTags) {
        let trimmedRFId = payload?.rfidTags.map((element) => {
          return element.trim();
        });
        payload.rfidTags = trimmedRFId;
      }
      if (payload?.rfidSTags) {
        let trimmedSRFId = payload?.rfidSTags.map((element) => {
          return element.trim();
        });
        payload.rfidSerialId = trimmedSRFId;
        delete payload?.rfidSTags;
      }
      const file = payload?.contractInfo.contractPDF;
      let contractPdfInByteArrayForm;
      if (file) {
        const _base64 = await getBase64(file);
        const [, base64] = _base64.split(',');

        contractPdfInByteArrayForm = Array.from(
          new Uint8Array(base64ToArrayBuffer(base64))
        );
      }
      if (payload?.paymentMethod == 'BANK_TRANSFER') {
        delete payload?.contractPaymentDetails?.contractMandateReference;
      }
      if (payload?.paymentMethod == 'SEPA') {
        delete payload?.contractPaymentDetails?.contractIban;
      }
      if (payload?.billingType === 'NO_BILLING') {
        delete payload?.contractPaymentDetails;
        delete payload?.paymentMethod;
      }
      const newPayload = {
        ...payload,
        contractInfo: {
          ...payload?.contractInfo,
          contractPdfInByteArrayForm,
          startDate: format(
            new Date(payload?.contractInfo.startDate),
            'yyyy-MM-dd'
          ),
          endDate: payload?.contractInfo?.endDate
            ? format(new Date(payload?.contractInfo?.endDate), 'yyyy-MM-dd')
            : null,
        },
        locationId: payload?.locationId
          ? payload?.locationId.split(':')[0]
          : null,
        authCode: contract?.authCode,
        cpoId: contract?.cpoId,
        contractKey: contract?.contractKey,
      };
      delete newPayload.contractInfo.contractPDF;

      const newPayloadEntries = Object.entries(newPayload)
        .map(([key, value]) => {
          if (!value) return null;
          if (typeof value === 'object' && !Array.isArray(value)) {
            for (const field in value) {
              if (
                value[field] === undefined ||
                value[field] === null ||
                value[field] === ''
              ) {
                delete value[field];
              }
            }
          }
          return [key, value];
        })
        .filter(Boolean);
      const filteredPayload = Object.fromEntries(newPayloadEntries);

      await contractManagement.put('/update-contract', filteredPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      openFeedback();
      mutate(['/contracts/operator', token, 'contractManagement']);
    } catch (error) {
      if (error?.error?.errorCodeResponse === errorCodes.rfIdExists) {
        setrfidError(error?.error?.errorDetail);
      } else if (
        error?.error?.errorCodeResponse === errorCodes.rfIdSerialNotExists
      ) {
        setrfidSError(error?.error?.errorDetail);
      } else if (
        error?.error?.errorCodeResponse === errorCodes.duplicateEmail
      ) {
        setemailError(true);
        setEndCustomerId(error?.error?.errorMessage);
      } else if (
        error?.error?.errorCodeResponse === errorCodes.dublicatedEndCustomerId
      ) {
        setDublicatedEndCustomerIdError(true);
      } else {
        setError(true);
      }
    }
  };

  const renderKey = (key) => {
    switch (key) {
      case 'EndCustomerId':
        return t('LBLCustomerNum');
      case 'ContractNumber':
        return t('ContractNumberLBL');
      case 'Kommunikationsadresse':
        return t('communicationadr');
      case 'Rechnungsnschrift':
        return t('billingadr');
      case 'Standort':
        return t('LBLLocation');
      case 'Parkplaznummer':
        return t('parkingnum');
      case 'Authentifizierung über':
        return t('LBLAuth');
      case 'Kostenpflichtig':
        return t('chargable');
      case 'Kostenfrei':
        return t('free');
      case 'Ladevorgangsgebühr':
        return t('fixTariffPerChargingRequest');
      case 'Bereitstellungsgebühr':
        return t('onboardingFee');
      case 'Nutzungs- und Ladestromvertrag':
        return t('usageandcurrentcontr');
      case 'Nutzungstarif':
        return t('tarif');
      case 'Ladestromtarif':
        return t('chargintarif');
      case 'Mandatsreferenz':
        return t('mandateref');
      case 'Debitorkonto-Nr.':
        return t('custacnum');
      case 'Kostenstellen-Nr':
        return t('costcnum');
      case 'Umsatzsteuerkennung':
        return t('vatId');
      case 'Zahlungsmethode':
        return t('paymethod');
      case 'Rechnung':
        return t('rechnung');
      case 'SEPA-Lastschrift':
        return t('sepa');
      case 'Stripe':
        return t('sepaStripemode');
      case 'Startdatum':
        return t('LBLStartDate');
      case 'RFID-serial-numbers':
        return t('rfIdSN');
      case 'Enddatum':
        return t('LBLEndDate');
      case 'UserType':
        return t('LBLUserType');
      case 'virtualIbanOfUser':
        return t('virtualIbanOfUser');
      case 'invoiceRemark':
        return t('invoiceremarks');
      case 'billingEmail':
        return t('billingEmail');
      case 'ContractTaxRate':
        return t('contractTaxRate');
      case 'Abrechnungszyklus':
        return t('LBLBillingCycle');
      default:
        return key;
    }
  };

  return (
    <>
      {Object.entries(renderData)
        .filter(([_, value]) => {
          if (value == null) return false; // null or undefined
          if (typeof value === 'string') return value.trim() !== '';
          if (Array.isArray(value)) return value.length > 0; // filter out []
          return true; // keep numbers, booleans, objects, etc.
        })
        .map(([key, value]) => (
          <Stack
            key={key}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            my={4}
          >
            <>
              <Typography variant="body1">{renderKey(key)}</Typography>
              {key === 'Parkplaznummer' ||
              key === 'RFID-Tags' ||
              key === 'RFID-serial-numbers' ? (
                <Typography variant="body1" align="right">
                  <OverflowText
                    label={value?.join(',')}
                    maxwidthValue={'150px'}
                  >
                    {' '}
                  </OverflowText>
                </Typography>
              ) : key === 'virtualIbanOfUser' ||
                key === 'Mandatsreferenz' ||
                key === 'Kommunikationsadresse' ||
                key === 'Rechnungsnschrift' ||
                key === 'invoiceRemark' ? (
                <Typography variant="body1" align="right">
                  <OverflowText label={value} maxwidthValue={'250px'}>
                    {' '}
                  </OverflowText>
                </Typography>
              ) : typeof value === 'string' ? (
                <Typography variant="body1" align="right" id={key}>
                  {value}
                </Typography>
              ) : (
                <Typography variant="body1" align="right" id={key}>
                  {value}
                </Typography>
              )}
            </>
          </Stack>
        ))}
    </>
  );
}
ConfirmUser.propTypes = {
  payload: PropTypes.objectOf(PropTypes.any).isRequired,
  openFeedback: PropTypes.func.isRequired,
};

export default ConfirmUser;
